import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Download, FileText, Search, Filter,
  Calendar, DollarSign, User, Tag, X, Save, Printer,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Building2, Heart, Church, Landmark, Globe, CreditCard,
  BarChart3, PieChart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const STORAGE_KEY = 'lfm_activity_log';

const colors = {
  forestGreen: '#1a4a2e',
  gold: '#c9a84c',
  lightGreen: '#2d6a4f',
  cream: '#faf8f5'
};

const ACTIVITY_TYPES = [
  'Food Distribution',
  'Outreach Event', 
  'Prayer Service',
  'Community Program',
  'Administrative',
  'Facility',
  'Donation Received',
  'Other'
];

const DONOR_TYPES = [
  'Individual',
  'Business',
  'Church',
  'Grant',
  'Government',
  'Online Platform',
  'Other'
];

const DONATION_METHODS = [
  'Cash',
  'Check',
  'Zelle',
  'PayPal',
  'CashApp',
  'Venmo',
  'Credit Card',
  'Bank Transfer',
  'Other'
];

const donorTypeIcons = {
  'Individual': User,
  'Business': Building2,
  'Church': Church,
  'Grant': Landmark,
  'Government': Landmark,
  'Online Platform': Globe,
  'Other': Heart
};

// Financial Reports Component
function FinancialReports({ activities, formatCurrency, colors }) {
  const [reportPeriod, setReportPeriod] = useState('monthly'); // monthly, quarterly, yearly
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Get available years from activities
  const availableYears = useMemo(() => {
    const years = new Set(activities.map(a => new Date(a.date).getFullYear()));
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [activities]);

  // Generate report data
  const reportData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const quarters = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];
    
    // Filter activities by year
    const yearActivities = activities.filter(a => new Date(a.date).getFullYear() === selectedYear);
    
    if (reportPeriod === 'monthly') {
      return months.map((month, index) => {
        const monthActivities = yearActivities.filter(a => new Date(a.date).getMonth() === index);
        const donations = monthActivities.filter(a => a.type === 'Donation Received');
        const expenses = monthActivities.filter(a => a.type !== 'Donation Received');
        
        const donationTotal = donations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
        const expenseTotal = expenses.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
        
        return {
          period: month,
          donations: donationTotal,
          expenses: expenseTotal,
          net: donationTotal - expenseTotal,
          donationCount: donations.length,
          expenseCount: expenses.length
        };
      });
    } else if (reportPeriod === 'quarterly') {
      return quarters.map((quarter, qIndex) => {
        const startMonth = qIndex * 3;
        const endMonth = startMonth + 2;
        
        const quarterActivities = yearActivities.filter(a => {
          const month = new Date(a.date).getMonth();
          return month >= startMonth && month <= endMonth;
        });
        
        const donations = quarterActivities.filter(a => a.type === 'Donation Received');
        const expenses = quarterActivities.filter(a => a.type !== 'Donation Received');
        
        const donationTotal = donations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
        const expenseTotal = expenses.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
        
        return {
          period: quarter,
          donations: donationTotal,
          expenses: expenseTotal,
          net: donationTotal - expenseTotal,
          donationCount: donations.length,
          expenseCount: expenses.length
        };
      });
    } else {
      // Yearly - show all available years
      return availableYears.map(year => {
        const yearActs = activities.filter(a => new Date(a.date).getFullYear() === year);
        const donations = yearActs.filter(a => a.type === 'Donation Received');
        const expenses = yearActs.filter(a => a.type !== 'Donation Received');
        
        const donationTotal = donations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
        const expenseTotal = expenses.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
        
        return {
          period: year.toString(),
          donations: donationTotal,
          expenses: expenseTotal,
          net: donationTotal - expenseTotal,
          donationCount: donations.length,
          expenseCount: expenses.length
        };
      });
    }
  }, [activities, reportPeriod, selectedYear, availableYears]);

  // Totals for the period
  const periodTotals = useMemo(() => {
    return reportData.reduce((acc, row) => ({
      donations: acc.donations + row.donations,
      expenses: acc.expenses + row.expenses,
      net: acc.net + row.net,
      donationCount: acc.donationCount + row.donationCount,
      expenseCount: acc.expenseCount + row.expenseCount
    }), { donations: 0, expenses: 0, net: 0, donationCount: 0, expenseCount: 0 });
  }, [reportData]);

  // Category breakdown
  const categoryBreakdown = useMemo(() => {
    const yearActivities = activities.filter(a => 
      reportPeriod === 'yearly' || new Date(a.date).getFullYear() === selectedYear
    );
    
    const breakdown = {};
    yearActivities.filter(a => a.type !== 'Donation Received').forEach(a => {
      breakdown[a.type] = (breakdown[a.type] || 0) + (parseFloat(a.amount) || 0);
    });
    
    return Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  }, [activities, reportPeriod, selectedYear]);

  // Max value for bar chart scaling
  const maxValue = useMemo(() => {
    const allValues = reportData.flatMap(r => [r.donations, r.expenses]);
    return Math.max(...allValues, 1);
  }, [reportData]);

  return (
    <div className="space-y-6">
      {/* Report Controls */}
      <div className="bg-white rounded-xl shadow-md p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-gray-400" />
          <select
            value={reportPeriod}
            onChange={(e) => setReportPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
            data-testid="report-period"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Year-over-Year</option>
          </select>
        </div>
        
        {reportPeriod !== 'yearly' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Year:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg"
              data-testid="report-year"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        )}

        <div className="ml-auto text-sm text-gray-500">
          {reportPeriod === 'yearly' ? 'All Years' : `${selectedYear} Report`}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Donations</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(periodTotals.donations)}</p>
            </div>
            <TrendingUp size={32} className="text-green-200" />
          </div>
          <p className="text-xs text-gray-400 mt-2">{periodTotals.donationCount} donations</p>
        </div>
        
        <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Expenses</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(periodTotals.expenses)}</p>
            </div>
            <TrendingDown size={32} className="text-red-200" />
          </div>
          <p className="text-xs text-gray-400 mt-2">{periodTotals.expenseCount} expenses</p>
        </div>
        
        <div className="bg-white rounded-xl p-5 shadow-md border-l-4" style={{ borderColor: colors.gold }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Net Balance</p>
              <p className={`text-2xl font-bold ${periodTotals.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(periodTotals.net)}
              </p>
            </div>
            <DollarSign size={32} style={{ color: `${colors.gold}50` }} />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {periodTotals.net >= 0 ? 'Surplus' : 'Deficit'}
          </p>
        </div>
        
        <div className="bg-white rounded-xl p-5 shadow-md border-l-4" style={{ borderColor: colors.forestGreen }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Per Period</p>
              <p className="text-2xl font-bold" style={{ color: colors.forestGreen }}>
                {formatCurrency(periodTotals.net / (reportData.length || 1))}
              </p>
            </div>
            <BarChart3 size={32} style={{ color: `${colors.forestGreen}30` }} />
          </div>
          <p className="text-xs text-gray-400 mt-2">{reportData.length} periods</p>
        </div>
      </div>

      {/* Bar Chart Visualization */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-bold mb-4" style={{ color: colors.forestGreen }}>
          Income vs Expenses
        </h3>
        <div className="space-y-4">
          {reportData.map((row, index) => (
            <div key={index} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700 w-24">{row.period}</span>
                <div className="flex-1 mx-4">
                  {/* Donations bar */}
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-16 text-xs text-right text-green-600">
                      {formatCurrency(row.donations)}
                    </div>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${(row.donations / maxValue) * 100}%` }}
                      />
                    </div>
                  </div>
                  {/* Expenses bar */}
                  <div className="flex items-center gap-2">
                    <div className="w-16 text-xs text-right text-red-600">
                      {formatCurrency(row.expenses)}
                    </div>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full transition-all duration-500"
                        style={{ width: `${(row.expenses / maxValue) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
                <span className={`w-24 text-right font-medium ${row.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {row.net >= 0 ? '+' : ''}{formatCurrency(row.net)}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span className="text-sm text-gray-600">Donations</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded" />
            <span className="text-sm text-gray-600">Expenses</span>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-bold mb-4" style={{ color: colors.forestGreen }}>
          Expense Categories
        </h3>
        {categoryBreakdown.length > 0 ? (
          <div className="space-y-3">
            {categoryBreakdown.map(([category, amount], index) => {
              const percentage = (amount / periodTotals.expenses) * 100 || 0;
              return (
                <div key={category} className="flex items-center gap-4">
                  <span className="w-32 text-sm text-gray-600 truncate">{category}</span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${percentage}%`,
                        backgroundColor: index === 0 ? colors.forestGreen : index === 1 ? colors.gold : `hsl(${index * 40}, 60%, 50%)`
                      }}
                    />
                  </div>
                  <span className="w-24 text-sm font-medium text-right">{formatCurrency(amount)}</span>
                  <span className="w-12 text-xs text-gray-400 text-right">{percentage.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No expense data for this period</p>
        )}
      </div>

      {/* Print Report Button */}
      <div className="flex justify-center">
        <Button
          onClick={() => window.print()}
          className="text-white"
          style={{ backgroundColor: colors.forestGreen }}
        >
          <Printer size={18} className="mr-2" />
          Print Financial Report
        </Button>
      </div>
    </div>
  );
}

export default function AdminActivityLog() {
  const [activities, setActivities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [activeTab, setActiveTab] = useState('log'); // log, reports
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    type: 'Food Distribution',
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    proofReference: '',
    submittedBy: '',
    // Donation-specific fields
    donorName: '',
    isAnonymous: false,
    donorType: 'Individual',
    donationMethod: 'Cash',
    designatedFor: 'General Fund',
    transactionId: '',
    dateReceived: new Date().toISOString().split('T')[0]
  });

  // Load from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setActivities(JSON.parse(stored));
    }
  }, []);

  // Save to localStorage
  const saveActivities = (newActivities) => {
    setActivities(newActivities);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newActivities));
  };

  // Filtered activities
  const filteredActivities = useMemo(() => {
    let filtered = [...activities];
    
    // Type filter
    if (typeFilter !== 'All') {
      filtered = filtered.filter(a => a.type === typeFilter);
    }
    
    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(a => a.date >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(a => a.date <= dateTo);
    }
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.name?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query) ||
        a.donorName?.toLowerCase().includes(query)
      );
    }
    
    // Sort by date (newest first)
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [activities, typeFilter, dateFrom, dateTo, searchQuery]);

  // Calculate totals
  const totals = useMemo(() => {
    const allDonations = activities.filter(a => a.type === 'Donation Received');
    const allExpenses = activities.filter(a => a.type !== 'Donation Received');
    
    const filteredDonations = filteredActivities.filter(a => a.type === 'Donation Received');
    const filteredExpenses = filteredActivities.filter(a => a.type !== 'Donation Received');
    
    // Donor type breakdown
    const donorTypeBreakdown = {};
    allDonations.forEach(d => {
      const type = d.donorType || 'Other';
      donorTypeBreakdown[type] = (donorTypeBreakdown[type] || 0) + (parseFloat(d.amount) || 0);
    });
    
    // Designation breakdown
    const designationBreakdown = {};
    allDonations.forEach(d => {
      const designation = d.designatedFor || 'General Fund';
      designationBreakdown[designation] = (designationBreakdown[designation] || 0) + (parseFloat(d.amount) || 0);
    });
    
    return {
      totalEntries: activities.length,
      filteredEntries: filteredActivities.length,
      totalDonations: allDonations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0),
      totalExpenses: allExpenses.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0),
      filteredDonations: filteredDonations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0),
      filteredExpenses: filteredExpenses.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0),
      donorTypeBreakdown,
      designationBreakdown
    };
  }, [activities, filteredActivities]);

  const netBalance = totals.totalDonations - totals.totalExpenses;
  const filteredNetBalance = totals.filteredDonations - totals.filteredExpenses;

  const handleAddActivity = () => {
    setEditingActivity(null);
    setFormData({
      name: '',
      type: 'Food Distribution',
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      proofReference: '',
      submittedBy: '',
      donorName: '',
      isAnonymous: false,
      donorType: 'Individual',
      donationMethod: 'Cash',
      designatedFor: 'General Fund',
      transactionId: '',
      dateReceived: new Date().toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setFormData({ ...activity });
    setShowModal(true);
  };

  const handleSaveActivity = () => {
    if (!formData.name || !formData.date || !formData.submittedBy) {
      toast.error('Please fill in required fields (Name, Date, Submitted By)');
      return;
    }

    let updated;
    const activityData = {
      ...formData,
      donorName: formData.isAnonymous ? 'Anonymous' : formData.donorName
    };

    if (editingActivity) {
      updated = activities.map(a => 
        a.id === editingActivity.id ? { ...activityData, id: a.id } : a
      );
      toast.success('Activity updated!');
    } else {
      const newActivity = {
        ...activityData,
        id: `act-${Date.now()}`
      };
      updated = [...activities, newActivity];
      toast.success('Activity added!');
    }

    saveActivities(updated);
    setShowModal(false);
  };

  const handleDeleteActivity = (activity) => {
    if (window.confirm(`Delete "${activity.name}"?`)) {
      const updated = activities.filter(a => a.id !== activity.id);
      saveActivities(updated);
      toast.success('Activity deleted');
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Date', 'Activity Name', 'Type', 'Description', 'Amount', 
      'Proof Reference', 'Submitted By', 'Donor Name', 'Donor Type',
      'Donation Method', 'Designated For', 'Transaction ID'
    ];
    
    const rows = filteredActivities.map(a => [
      a.date, a.name, a.type, a.description, a.amount || '0',
      a.proofReference, a.submittedBy, 
      a.type === 'Donation Received' ? (a.donorName || '') : '',
      a.type === 'Donation Received' ? (a.donorType || '') : '',
      a.type === 'Donation Received' ? (a.donationMethod || '') : '',
      a.type === 'Donation Received' ? (a.designatedFor || '') : '',
      a.type === 'Donation Received' ? (a.transactionId || '') : ''
    ]);
    
    // Add totals row
    rows.push([]);
    rows.push(['', '', '', 'TOTALS', '', '', '', '', '', '', '', '']);
    rows.push(['', '', '', 'Total Donations:', totals.filteredDonations.toFixed(2), '', '', '', '', '', '', '']);
    rows.push(['', '', '', 'Total Expenses:', totals.filteredExpenses.toFixed(2), '', '', '', '', '', '', '']);
    rows.push(['', '', '', 'Net Balance:', filteredNetBalance.toFixed(2), '', '', '', '', '', '', '']);
    
    const csv = [headers, ...rows].map(row => 
      row.map(cell => `"${(cell?.toString() || '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lfm-activity-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported!');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Print/PDF View
  if (showPrintView) {
    return (
      <div className="bg-white min-h-screen p-8 print:p-4">
        <style>{`
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-print { display: none !important; }
          }
        `}</style>
        
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 border-b-2 pb-4" style={{ borderColor: colors.forestGreen }}>
            <h1 className="text-3xl font-bold" style={{ color: colors.forestGreen }}>
              Living Fruit Ministry
            </h1>
            <h2 className="text-xl mt-2" style={{ color: colors.gold }}>
              Activity & Expense Report
            </h2>
            <p className="text-gray-600 mt-2">
              {dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : `As of ${new Date().toLocaleDateString()}`}
            </p>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-8 p-4 rounded-lg" style={{ backgroundColor: `${colors.forestGreen}10` }}>
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Donations</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totals.filteredDonations)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Expenses</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(totals.filteredExpenses)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Net Balance</p>
              <p className={`text-xl font-bold ${filteredNetBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(filteredNetBalance)}
              </p>
            </div>
          </div>

          {/* Donations Section */}
          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.forestGreen }}>
              Donations Received ({filteredActivities.filter(a => a.type === 'Donation Received').length})
            </h3>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.forestGreen }} className="text-white">
                  <th className="p-2 text-left border">Date</th>
                  <th className="p-2 text-left border">Donor</th>
                  <th className="p-2 text-left border">Type</th>
                  <th className="p-2 text-left border">Method</th>
                  <th className="p-2 text-left border">Designation</th>
                  <th className="p-2 text-right border">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities.filter(a => a.type === 'Donation Received').map((a, i) => (
                  <tr key={a.id} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-2 border">{a.date}</td>
                    <td className="p-2 border">{a.donorName || 'Anonymous'}</td>
                    <td className="p-2 border">{a.donorType}</td>
                    <td className="p-2 border">{a.donationMethod}</td>
                    <td className="p-2 border">{a.designatedFor}</td>
                    <td className="p-2 border text-right font-medium">{formatCurrency(a.amount)}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: `${colors.gold}30` }}>
                  <td colSpan={5} className="p-2 border text-right font-bold">Total Donations:</td>
                  <td className="p-2 border text-right font-bold text-green-700">{formatCurrency(totals.filteredDonations)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Expenses Section */}
          <div className="mb-8">
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.forestGreen }}>
              Expenses ({filteredActivities.filter(a => a.type !== 'Donation Received').length})
            </h3>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.forestGreen }} className="text-white">
                  <th className="p-2 text-left border">Date</th>
                  <th className="p-2 text-left border">Activity</th>
                  <th className="p-2 text-left border">Type</th>
                  <th className="p-2 text-left border">Description</th>
                  <th className="p-2 text-left border">Proof</th>
                  <th className="p-2 text-right border">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities.filter(a => a.type !== 'Donation Received').map((a, i) => (
                  <tr key={a.id} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-2 border">{a.date}</td>
                    <td className="p-2 border">{a.name}</td>
                    <td className="p-2 border">{a.type}</td>
                    <td className="p-2 border">{a.description?.substring(0, 50)}</td>
                    <td className="p-2 border">{a.proofReference}</td>
                    <td className="p-2 border text-right font-medium">{formatCurrency(a.amount)}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: `${colors.gold}30` }}>
                  <td colSpan={5} className="p-2 border text-right font-bold">Total Expenses:</td>
                  <td className="p-2 border text-right font-bold text-red-700">{formatCurrency(totals.filteredExpenses)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Grand Total */}
          <div className="p-4 rounded-lg text-center" style={{ backgroundColor: colors.forestGreen }}>
            <p className="text-white text-lg">Net Balance</p>
            <p className={`text-3xl font-bold ${filteredNetBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(filteredNetBalance)}
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-4 border-t text-center text-gray-500 text-sm">
            <p>Generated on {new Date().toLocaleString()}</p>
            <p>Living Fruit Ministry • Financial Activity Report</p>
          </div>

          {/* Print Button */}
          <div className="no-print mt-8 flex justify-center gap-4">
            <Button onClick={() => window.print()} style={{ backgroundColor: colors.forestGreen }} className="text-white">
              <Printer size={18} className="mr-2" />
              Print Report
            </Button>
            <Button onClick={() => setShowPrintView(false)} variant="outline">
              Back to Log
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-32" data-testid="admin-activity-log">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl" style={{ color: colors.forestGreen }}>
            Activity & Expense Log
          </h1>
          <p className="text-gray-500 mt-1">Track ministry activities, expenses, and donations</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleAddActivity}
            className="text-white"
            style={{ backgroundColor: colors.forestGreen }}
            data-testid="add-activity-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Activity
          </Button>
          <Button
            onClick={exportToCSV}
            variant="outline"
            style={{ borderColor: colors.forestGreen, color: colors.forestGreen }}
            data-testid="export-csv-btn"
          >
            <Download size={18} className="mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => setShowPrintView(true)}
            variant="outline"
            style={{ borderColor: colors.gold, color: colors.forestGreen }}
            data-testid="print-report-btn"
          >
            <Printer size={18} className="mr-2" />
            IRS Report
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
        <button
          onClick={() => setActiveTab('log')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'log' ? 'text-white shadow-md' : 'text-gray-600 hover:bg-gray-200'
          }`}
          style={activeTab === 'log' ? { backgroundColor: colors.forestGreen } : {}}
          data-testid="tab-log"
        >
          <FileText size={18} />
          Activity Log
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'reports' ? 'text-white shadow-md' : 'text-gray-600 hover:bg-gray-200'
          }`}
          style={activeTab === 'reports' ? { backgroundColor: colors.forestGreen } : {}}
          data-testid="tab-reports"
        >
          <BarChart3 size={18} />
          Financial Reports
        </button>
      </div>

      {/* Financial Reports Tab */}
      {activeTab === 'reports' && (
        <FinancialReports activities={activities} formatCurrency={formatCurrency} colors={colors} />
      )}

      {/* Activity Log Tab */}
      {activeTab === 'log' && (
      <>
      {/* Donations Summary Panel */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-6 border border-green-200">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: colors.forestGreen }}>
          <TrendingUp size={20} />
          Donations Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Donations */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500 mb-1">Total Donations Received</p>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(totals.totalDonations)}</p>
          </div>
          
          {/* By Donor Type */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500 mb-2">By Donor Type</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {Object.entries(totals.donorTypeBreakdown).sort((a, b) => b[1] - a[1]).map(([type, amount]) => {
                const Icon = donorTypeIcons[type] || Heart;
                return (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-gray-600">
                      <Icon size={14} />
                      {type}
                    </span>
                    <span className="font-medium">{formatCurrency(amount)}</span>
                  </div>
                );
              })}
              {Object.keys(totals.donorTypeBreakdown).length === 0 && (
                <p className="text-gray-400 text-sm">No donations yet</p>
              )}
            </div>
          </div>
          
          {/* By Designation */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500 mb-2">By Designation</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {Object.entries(totals.designationBreakdown).sort((a, b) => b[1] - a[1]).map(([designation, amount]) => (
                <div key={designation} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 truncate mr-2">{designation}</span>
                  <span className="font-medium">{formatCurrency(amount)}</span>
                </div>
              ))}
              {Object.keys(totals.designationBreakdown).length === 0 && (
                <p className="text-gray-400 text-sm">No donations yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
              style={{ focusRing: colors.forestGreen }}
              data-testid="type-filter"
            >
              <option value="All">All Types</option>
              {ACTIVITY_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-gray-400" />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-36"
              placeholder="From"
              data-testid="date-from"
            />
            <span className="text-gray-400">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-36"
              placeholder="To"
              data-testid="date-to"
            />
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Clear Filters */}
          {(typeFilter !== 'All' || dateFrom || dateTo || searchQuery) && (
            <Button
              variant="ghost"
              onClick={() => {
                setTypeFilter('All');
                setDateFrom('');
                setDateTo('');
                setSearchQuery('');
              }}
              className="text-gray-500"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Activity Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: colors.forestGreen }} className="text-white">
                <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Activity Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Proof</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Submitted By</th>
                <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivities.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-lg">No activities found</p>
                    <p className="text-sm">Add your first activity to get started</p>
                  </td>
                </tr>
              ) : (
                filteredActivities.map((activity, i) => (
                  <motion.tr
                    key={activity.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`border-b ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}
                  >
                    <td className="px-4 py-3 text-sm">{activity.date}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{activity.name}</p>
                      {activity.type === 'Donation Received' && activity.donorName && (
                        <p className="text-xs text-gray-500">From: {activity.donorName}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span 
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          activity.type === 'Donation Received' 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {activity.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                      {activity.description}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium text-right ${
                      activity.type === 'Donation Received' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {activity.type === 'Donation Received' ? '+' : '-'}{formatCurrency(activity.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{activity.proofReference}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{activity.submittedBy}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEditActivity(activity)}
                          className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity(activity)}
                          className="p-2 rounded-lg hover:bg-red-100 text-red-500"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* Sticky Totals Bar */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-40 shadow-2xl"
        style={{ backgroundColor: colors.forestGreen }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-white/70 text-xs">Total Entries</p>
                <p className="text-white font-bold text-lg">{totals.filteredEntries}</p>
              </div>
              <div className="h-10 w-px bg-white/20" />
              <div>
                <p className="text-white/70 text-xs flex items-center gap-1">
                  <ArrowUpRight size={12} />
                  Donations In
                </p>
                <p className="text-lg font-bold" style={{ color: '#4ade80' }}>
                  {formatCurrency(totals.filteredDonations)}
                </p>
              </div>
              <div>
                <p className="text-white/70 text-xs flex items-center gap-1">
                  <ArrowDownRight size={12} />
                  Expenses Out
                </p>
                <p className="text-lg font-bold text-red-400">
                  {formatCurrency(totals.filteredExpenses)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/70 text-xs">Net Balance</p>
              <p 
                className="text-2xl font-bold"
                style={{ color: filteredNetBalance >= 0 ? colors.gold : '#ef4444' }}
              >
                {formatCurrency(filteredNetBalance)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div 
                className="px-6 py-4 flex items-center justify-between sticky top-0"
                style={{ backgroundColor: colors.forestGreen }}
              >
                <h2 className="text-xl font-bold text-white">
                  {editingActivity ? 'Edit Activity' : 'Add New Activity'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Activity Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Activity Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Weekly Food Distribution"
                    data-testid="modal-name"
                  />
                </div>

                {/* Activity Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Activity Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={e => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                    data-testid="modal-type"
                  >
                    {ACTIVITY_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Donation-specific fields */}
                {formData.type === 'Donation Received' && (
                  <div className="bg-green-50 rounded-xl p-4 space-y-4 border border-green-200">
                    <h3 className="font-medium text-green-800 flex items-center gap-2">
                      <DollarSign size={18} />
                      Donation Details
                    </h3>

                    {/* Donor Name with Anonymous */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Donor Name
                      </label>
                      <div className="flex items-center gap-3">
                        <Input
                          value={formData.donorName}
                          onChange={e => setFormData({ ...formData, donorName: e.target.value })}
                          placeholder="Donor name"
                          disabled={formData.isAnonymous}
                          className="flex-1"
                          data-testid="modal-donor-name"
                        />
                        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={formData.isAnonymous}
                            onChange={e => setFormData({ ...formData, isAnonymous: e.target.checked, donorName: e.target.checked ? 'Anonymous' : '' })}
                            className="w-4 h-4"
                            data-testid="modal-anonymous"
                          />
                          Anonymous
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Donor Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Donor Type
                        </label>
                        <select
                          value={formData.donorType}
                          onChange={e => setFormData({ ...formData, donorType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          data-testid="modal-donor-type"
                        >
                          {DONOR_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      {/* Donation Method */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Donation Method
                        </label>
                        <select
                          value={formData.donationMethod}
                          onChange={e => setFormData({ ...formData, donationMethod: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          data-testid="modal-donation-method"
                        >
                          {DONATION_METHODS.map(method => (
                            <option key={method} value={method}>{method}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Designated For */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Designated For
                      </label>
                      <Input
                        value={formData.designatedFor}
                        onChange={e => setFormData({ ...formData, designatedFor: e.target.value })}
                        placeholder="e.g., Food Pantry, General Fund, Building Fund"
                        data-testid="modal-designated-for"
                      />
                    </div>

                    {/* Transaction ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Check # or Transaction ID (optional)
                      </label>
                      <Input
                        value={formData.transactionId}
                        onChange={e => setFormData({ ...formData, transactionId: e.target.value })}
                        placeholder="e.g., Check #1234 or TXN-ABC123"
                        data-testid="modal-transaction-id"
                      />
                    </div>
                  </div>
                )}

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                    data-testid="modal-date"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description / Notes
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Activity details..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg min-h-[80px]"
                    data-testid="modal-description"
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.type === 'Donation Received' ? 'Amount Received $' : 'Amount Spent $ (optional)'}
                  </label>
                  <div className="relative">
                    <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={e => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      className="pl-10"
                      data-testid="modal-amount"
                    />
                  </div>
                </div>

                {/* Proof Reference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt/Proof Label
                  </label>
                  <Input
                    value={formData.proofReference}
                    onChange={e => setFormData({ ...formData, proofReference: e.target.value })}
                    placeholder="e.g., Receipt #1042 or Bank statement 3/15"
                    data-testid="modal-proof"
                  />
                </div>

                {/* Submitted By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Submitted By *
                  </label>
                  <Input
                    value={formData.submittedBy}
                    onChange={e => setFormData({ ...formData, submittedBy: e.target.value })}
                    placeholder="Staff member name"
                    data-testid="modal-submitted-by"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setShowModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveActivity}
                    className="flex-1 text-white"
                    style={{ backgroundColor: colors.forestGreen }}
                    data-testid="modal-save-btn"
                  >
                    <Save size={18} className="mr-2" />
                    Save Activity
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
